import { watch } from 'vue'
import { consola } from 'consola'
import { appearanceCssVars } from '@shared/config'
import { useAppearance } from '@entities/institution'

const logger = consola.withTag('appearance')

const BRAND_FONT_LINK_ID = 'app-brand-font'

/**
 * Применяет конфиг внешнего вида заведения к документу.
 *
 * Скругления и акценты живут в CSS-переменных на <html> — так один и тот же
 * набор стилей (assets/css/product-cards.scss) обслуживает и витрину, и превью
 * в админке, а смена настройки не требует пересборки.
 *
 * Плагин клиентский: настройки приезжают вместе с заведением уже после гидрации,
 * а до этого действуют дефолты из shared/config/appearance.ts — те самые,
 * что зашиты в SCSS как fallback-значения переменных.
 */
export default defineNuxtPlugin(() => {
  const { appearance } = useAppearance()

  const applyFont = (family: string, cssUrl: string) => {
    const existing = document.querySelector<HTMLLinkElement>(`#${BRAND_FONT_LINK_ID}`)

    if (!family || !cssUrl) {
      existing?.remove()
      return
    }
    if (existing?.href === cssUrl) return

    // Ссылку не переиспользуем, а заменяем: иначе на каждой смене адреса
    // на том же элементе копился бы ещё один обработчик error.
    existing?.remove()
    const link = document.createElement('link')
    link.id = BRAND_FONT_LINK_ID
    link.rel = 'stylesheet'
    link.href = cssUrl
    // Шрифт может не подгрузиться (битая ссылка, блокировщик) — витрина обязана
    // пережить это на системном шрифте, а не остаться без текста.
    link.addEventListener('error', () => logger.warn('Фирменный шрифт не загрузился:', cssUrl))
    document.head.append(link)
  }

  // Какие переменные мы поставили в прошлый раз. Нужно, чтобы снимать те, что
  // пропали из конфига: appearanceCssVars просто не возвращает выключенные настройки,
  // а `style` элемента ничего не забывает сам — выключенный фон или обводка
  // иначе продолжали бы действовать до перезагрузки страницы.
  let appliedVars: string[] = []

  watch(
    appearance,
    (config) => {
      const root = document.documentElement
      const vars = appearanceCssVars(config)

      appliedVars
        .filter((name) => !(name in vars))
        .forEach((name) => root.style.removeProperty(name))
      appliedVars = Object.keys(vars)

      Object.entries(vars).forEach(([name, value]) => {
        root.style.setProperty(name, value)
      })

      // Пресет раскладки — атрибутом, а не переменной: пороги живут в медиазапросах,
      // а их из custom property не собрать (см. assets/css/appearance-layout.scss).
      root.dataset.appLayout = config.layout.grid

      applyFont(
        config.brandFont.enabled ? config.brandFont.family : '',
        config.brandFont.enabled ? config.brandFont.cssUrl : '',
      )
    },
    { immediate: true, deep: true },
  )
})
